import { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PaperPlaneRight,
  ChatCircle,
  SignOut,
  ShieldCheck,
  Lightning,
  GearSix,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UsageBar } from "@/components/UsageBar";

export const ChatPane = ({
  user, messages, inputMessage, setInputMessage,
  isLoading, sendMessage, logout, usage,
  isAdmin, onUpgradeClick, onManageSubscription,
}) => {
  const messagesEndRef = useRef(null);

  return (
    <div className="w-full md:w-1/2 h-1/2 md:h-full border-r border-border flex flex-col">
      {/* Chat Header */}
      <div className="border-b border-border p-6 bg-surface sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ChatCircle size={28} weight="duotone" className="text-foreground" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="chat-header-title">BusyBodies AI</h1>
              <p className="text-sm text-muted-foreground" data-testid="chat-header-subtitle">Your AI assistant for tasks & spiritual book creation</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <span className="flex items-center gap-1 text-xs text-green-400 border border-green-800 px-2 py-1" data-testid="admin-badge">
                <ShieldCheck size={14} weight="fill" /> Admin
              </span>
            )}
            {user.role === "pro" && (
              <span className="flex items-center gap-1 text-xs text-yellow-400 border border-yellow-800 px-2 py-1" data-testid="pro-badge">
                <Lightning size={14} weight="fill" /> Pro
              </span>
            )}
            <span className="text-xs text-neutral-500" data-testid="user-email">{user.email}</span>
            <Button size="sm" variant="ghost" onClick={onManageSubscription} className="text-neutral-400 hover:text-white" data-testid="manage-subscription-button">
              <GearSix size={18} />
            </Button>
            <Button size="sm" variant="ghost" onClick={logout} className="text-neutral-400 hover:text-white" data-testid="logout-button">
              <SignOut size={18} />
            </Button>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages-container">
        <AnimatePresence>
          {messages.map((msg, idx) => (
            <motion.div key={msg.id || `${msg.role}-${msg.timestamp}-${idx}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${msg.role}-${idx}`}>
              <div className={`max-w-[80%] p-4 ${msg.role === "user" ? "bg-secondary border border-border" : "bg-transparent border border-border/40"}`}>
                <p className="text-sm leading-relaxed" data-testid={`message-content-${idx}`}>{msg.content}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {isLoading && (
          <div className="flex justify-start" data-testid="loading-indicator">
            <div className="bg-transparent border border-border/40 p-4">
              <p className="text-sm text-muted-foreground">AI is thinking...</p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Usage Bar for non-admin */}
      {!isAdmin && <UsageBar usage={usage} onUpgradeClick={onUpgradeClick} />}

      {/* Chat Input */}
      <div className="border-t border-border p-6 bg-surface" data-testid="chat-input-container">
        <form onSubmit={sendMessage} className="flex gap-2">
          <Input value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} placeholder="Type your message..." className="flex-1 bg-transparent border-border focus:ring-1 focus:ring-white" disabled={isLoading} data-testid="chat-input-field" />
          <Button type="submit" disabled={isLoading || !inputMessage.trim()} className="bg-white text-black hover:bg-gray-200 px-6" data-testid="chat-send-button">
            <PaperPlaneRight size={20} weight="bold" />
          </Button>
        </form>
      </div>
    </div>
  );
};
